import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { formatShortDate } from '../utils/dateUtils';
import './TodayQR.css';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

// Full-screen one-tap QR for today's class. Bookmark `/qr/today` on the
// instructor's phone; the page resolves today's playlist (MT timezone)
// and renders the rider URL as a giant QR.
//
// States:
//   - loading
//   - no class today (rare; we still render something useful)
//   - exactly one class today → instant QR
//   - more than one class today → small picker first, then QR
//
// Honours the dev-only ?_now=<ISO> override the rest of the ratings
// system uses, for simulation/screenshots.
function TodayQR() {
  const [search] = useSearchParams();
  const fakeNow = search.get('_now') || null;
  const qsForNow = fakeNow ? `?_now=${encodeURIComponent(fakeNow)}` : '';

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [today, setToday] = useState(null);
  const [classes, setClasses] = useState([]);
  const [picked, setPicked] = useState(null); // when multiple classes

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API_BASE}/ratings/today${qsForNow}`)
      .then((r) => {
        if (cancelled) return;
        setToday(r.data.today);
        setClasses(r.data.classes || []);
        if ((r.data.classes || []).length === 1) {
          setPicked(r.data.classes[0]);
        }
      })
      .catch((e) => {
        if (!cancelled) setErr(e.response?.data?.error || 'Failed to load');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [qsForNow]);

  if (loading) {
    return (
      <div className="tq-shell">
        <div className="tq-spinner" />
      </div>
    );
  }

  if (err) {
    return (
      <div className="tq-shell">
        <div className="tq-msg">
          <div className="tq-msg-icon">😕</div>
          <h1>{err}</h1>
        </div>
      </div>
    );
  }

  if (classes.length === 0) {
    return (
      <div className="tq-shell">
        <div className="tq-msg">
          <div className="tq-msg-icon">🌙</div>
          <h1>No class today</h1>
          <p>Nothing on the schedule for {today}.</p>
        </div>
      </div>
    );
  }

  // Multiple classes today and none picked yet → picker
  if (classes.length > 1 && !picked) {
    return (
      <div className="tq-shell">
        <div className="tq-msg">
          <h1>Which class?</h1>
          <p>Two classes today — tap the one you just taught.</p>
          <div className="tq-picker">
            {classes.map((c) => (
              <button
                key={c.id}
                type="button"
                className="tq-picker-btn"
                onClick={() => setPicked(c)}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Show the QR
  const c = picked;
  const rateUrl = `${window.location.origin}/rate/${c.id}${qsForNow}`;
  const display = c.name || formatShortDate(c.date);
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=600x600&margin=0&data=${encodeURIComponent(rateUrl)}`;

  return (
    <div className="tq-shell">
      <div className="tq-card">
        <div className="tq-tag">Rating window · 9:30am – 12:00pm MT</div>
        <h1 className="tq-title">{display}</h1>
        <div className="tq-sub">Scan to rate today's ride</div>
        <div className="tq-qr-wrap">
          <img alt={`QR for ${display}`} src={qrSrc} />
        </div>
        <div className="tq-url">{rateUrl.replace(/^https?:\/\//, '')}</div>
        {classes.length > 1 && (
          <button
            type="button"
            className="tq-switch"
            onClick={() => setPicked(null)}
          >
            ← Pick a different class
          </button>
        )}
      </div>
    </div>
  );
}

export default TodayQR;
